import {
  FoodDetailsBody,
  FoodDetailsHeader,
  FoodDetailsInfo,
  StyledFoodDetails,
} from "./styled"
import { BackButton } from "components/Common/BackButton"
import Button from "components/Buttons/Button"
import TrashIcon from "components/Icons/TrashIcon"
import { useState } from "react"
import ChevronLeftBtn from "components/Icons/ChevronLeftBtn"
import useEdamaForm from "features/food/hooks/edamaFood/useEdamaDetailsForm"
import EdamaNameAndPortion from "../share/EdamaNameAndPortion"
import { EdamamEntry } from "model/food/types"
import FoodImage from "../share/FoodImage"
import AppStyleCtaBox from "components/Common/AppStyleCtaBox"
import { TopNavWrapper } from "components/Layouts/TopNavWrapper"
import EditNutrientForm from "../share/EditNutrientForm"
import EditableNutrientInformation from "../share/EditableNutrientInformation"
import NutrientInformation from "../share/NutrientInformation"

interface IProps<T extends EdamamEntry> {
  onBack: () => void
  food: T
  onEdit?: (food: T) => void
  onDelete?: () => void
  onAdd?: (food: T) => void
  mode?: "edit" | "add"
  isIngredient?: boolean
}

const EdamaFoodDetails = <T extends EdamamEntry>(props: IProps<T>) => {
  const [displaySection, setDisplaySection] = useState<
    "FOOD_FORM" | "EDIT_NUTRITION_FORM"
  >("FOOD_FORM")
  const { onBack, food, onEdit, onDelete, mode, onAdd, isIngredient } = props
  const { edama, onPortionChange, onUnitChange, updateFoodAsManualNutrition } =
    useEdamaForm(food)
  const [isPortionValid, setIsPortionValid] = useState(true)

  const onPortionChangeHandler = (value: number) => {
    onPortionChange(value)
    setIsPortionValid(true)
  }

  if (displaySection === "EDIT_NUTRITION_FORM") {
    return (
      <EditNutrientForm
        onClose={() => {
          setDisplaySection("FOOD_FORM")
        }}
        meal={edama}
        onSubmit={updateFoodAsManualNutrition}
      />
    )
  }

  return (
    <StyledFoodDetails>
      <FoodDetailsBody>
        <FoodImage previewImageUrl={food.previewImageUrl}>
          <TopNavWrapper>
            <FoodDetailsHeader>
              <BackButton onClick={onBack}>
                <ChevronLeftBtn />
              </BackButton>
            </FoodDetailsHeader>
          </TopNavWrapper>
        </FoodImage>

        <FoodDetailsInfo>
          <EdamaNameAndPortion
            edama={edama}
            unitValue={edama.unit}
            portionValue={edama.portion}
            onPortionChange={onPortionChangeHandler}
            onUnitChange={onUnitChange}
            unitOptions={edama.units}
            setIsPortionValid={setIsPortionValid}
            calories={edama.calorie}
            gram={edama.gram}
          />

          {isIngredient ? (
            <NutrientInformation entry={edama} />
          ) : (
            <EditableNutrientInformation
              entry={edama}
              setEditMode={() => setDisplaySection("EDIT_NUTRITION_FORM")}
            />
          )}
        </FoodDetailsInfo>
      </FoodDetailsBody>

      {mode === "edit" && (
        <AppStyleCtaBox>
          <Button
            onClick={() => onDelete?.()}
            $type="default"
            $isDeleteBtn
            style={{
              padding: "var(--spacing_m, 12px) var(--spacing_l, 16px)",
            }}
          >
            <TrashIcon color="#FF4921" />
          </Button>

          <Button
            disabled={!isPortionValid}
            style={{ width: "100%" }}
            $type="primary"
            onClick={() => {
              onEdit?.(edama)
              onBack()
            }}
          >
            Save
          </Button>
        </AppStyleCtaBox>
      )}

      {mode === "add" && (
        <AppStyleCtaBox>
          <Button
            onClick={() => {
              onAdd?.(edama)
              onBack()
            }}
            disabled={!isPortionValid}
            style={{ width: "100%" }}
            $type="primary"
          >
            Add this item
          </Button>
        </AppStyleCtaBox>
      )}
    </StyledFoodDetails>
  )
}

export default EdamaFoodDetails
